import { useContext } from 'react'
import { useRouter as useCompatRouter } from 'next/compat/router'
import { usePathname } from 'next/navigation'
import { AppRouterContext } from 'next/dist/shared/lib/app-router-context.shared-runtime'

/**
 * Cross-router pathname hook (Phase 4c slice 4.5, #77).
 *
 * The shared chrome (Header/Footer/RegionSwitcher) and the region hooks
 * (useCurrentRegion/useUrlRegion) need the current URL PATHNAME (no query) and
 * must render under BOTH routers:
 *   - Pages Router: the legacy `next/router` is mounted; its `asPath` is the
 *     source of truth (unchanged behaviour).
 *   - App Router: the legacy router is NOT mounted, so `next/router`'s
 *     `useRouter()` throws "NextRouter was not mounted". The App Router pathname
 *     comes from `next/navigation`'s `usePathname()` instead.
 *
 * `next/compat/router` is the canonical incremental-migration primitive: its
 * `useRouter()` returns the Pages router when one exists and `null` (NO throw)
 * under App Router. We pair it with `usePathname()` and branch on the RESULT:
 *
 * BOTH hooks are called UNCONDITIONALLY every render (Rules of Hooks). We only
 * branch on their return values — never conditionally call a hook.
 *
 * Returns the pathname WITHOUT the query string (matching the previous
 * `router.asPath.split('?')[0]` usages), and `'/'` as the safe default.
 */
export default function usePathnameCompat() {
  const compatRouter = useCompatRouter()
  const appPathname = usePathname()

  if (compatRouter) {
    // Pages Router: strip the query off asPath (as the callers did before).
    return (compatRouter.asPath ? compatRouter.asPath.split('?')[0] : '/') || '/'
  }

  // App Router: usePathname() already excludes the query string.
  return appPathname || '/'
}

/**
 * True when a Pages Router is mounted, false under App Router (Phase 4c slice
 * 4.5, #77). `next/compat/router`'s useRouter() is the null-not-throw signal:
 * it returns the router under Pages and null under App Router. Use this to gate
 * Pages-only APIs (e.g. `next/head`, which crashes under App Router SSR) so the
 * shared chrome renders under both routers.
 */
export function useIsPagesRouter() {
  const compatRouter = useCompatRouter()
  // App Router context presence is the AUTHORITATIVE App-Router signal. Under the
  // App Router error/not-found render path (app/not-found.js), next/compat/router
  // can return a truthy router even though we are under App Router — which made
  // Layout render <Head> (next/head) and CRASH its useContext in the app-page
  // runtime (digest error on the 404). Treat "App Router context is mounted" as
  // App Router unconditionally, so next/head is gated off wherever App Router
  // owns the tree. Both hooks run every render (Rules of Hooks); we branch on the
  // results only.
  const appRouter = useContext(AppRouterContext)
  if (appRouter) return false
  return Boolean(compatRouter)
}

/**
 * Cross-router client-side navigation (Phase 4c slice 4.5, #77).
 *
 * Returns a `push(href)` function that navigates under BOTH routers:
 *   - Pages Router: `next/compat/router`'s router (present) → `router.push`.
 *   - App Router: the compat router is null; use the App Router instance read
 *     from `AppRouterContext` → `appRouter.push`.
 *
 * We read the App Router instance via `useContext(AppRouterContext)` rather than
 * `next/navigation`'s `useRouter()` because the latter THROWS
 * ("invariant expected app router to be mounted") under Pages Router, whereas
 * the raw context read returns `null` (no throw) — the same null-not-throw
 * primitive `usePathname()`/`next/compat/router` rely on. Both reads happen
 * UNCONDITIONALLY every render (Rules of Hooks); we branch only on the results.
 *
 * `push` is a no-op if neither router is available (defensive; should not happen
 * inside a mounted React tree).
 */
export function useNavigateCompat() {
  const compatRouter = useCompatRouter()
  const appRouter = useContext(AppRouterContext)

  const router = compatRouter || appRouter
  return {
    push: (href) => {
      if (router && typeof router.push === 'function') {
        router.push(href)
      }
    },
  }
}
